import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { useFetch, money } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Utensils, Save } from "lucide-react";

const Rate = ({ label, value, testid, children }) => (
  <div className="rounded-md border border-border p-3 bg-background" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{label}</p>
    {children || <p className="font-heading font-extrabold text-xl">{value == null ? "—" : money(value)}</p>}
  </div>
);

export function AdminCantineRates({ isMaster }) {
  const { data, reload } = useFetch(() => api.get("/cantine/rates"));
  const [f, setF] = useState({ daily: "", weekly: "" });
  useEffect(() => { if (data) setF({ daily: data.daily || "", weekly: data.weekly || "" }); }, [data]);
  const save = async () => {
    try { await api.put("/cantine/rates", { daily: Number(f.daily) || 0, weekly: Number(f.weekly) || 0 }); toast.success("Tarifs enregistrés"); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6" data-testid="cantine-rates">
      <p className="font-heading font-bold mb-1 flex items-center gap-2"><Utensils className="h-5 w-5 text-primary" />Tarifs de la cantine</p>
      <p className="text-xs text-muted-foreground mb-3">{isMaster ? "Seul l'Admin principal peut modifier les tarifs journalier et hebdomadaire." : "Lecture seule — seul l'Admin principal peut modifier ces tarifs."}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-stretch">
        <Rate label="Tarif journalier ($)" value={data?.daily} testid="cantine-rate-daily">
          {isMaster && <Input type="number" min="0" step="0.01" className="h-9 mt-1" value={f.daily} onChange={(e) => setF({ ...f, daily: e.target.value })} data-testid="cantine-daily-input" />}
        </Rate>
        <Rate label="Tarif hebdomadaire ($)" value={data?.weekly} testid="cantine-rate-weekly">
          {isMaster && <Input type="number" min="0" step="0.01" className="h-9 mt-1" value={f.weekly} onChange={(e) => setF({ ...f, weekly: e.target.value })} data-testid="cantine-weekly-input" />}
        </Rate>
        <Rate label="Tarif mensuel ($) — page Frais, inchangé" value={data?.monthly} testid="cantine-rate-monthly" />
      </div>
      {isMaster && <Button size="sm" className="mt-3" onClick={save} data-testid="cantine-rates-save"><Save className="h-4 w-4 mr-1" />Enregistrer les tarifs</Button>}
    </div>
  );
}

export function ParentCantineOptions({ kids: allKids, onOrdered }) {
  const kids = (allKids || []).filter((k) => !k.status || k.status === "accepted");
  const { data } = useFetch(() => api.get("/cantine/rates"));
  const [kid, setKid] = useState("");
  const [busy, setBusy] = useState(null);
  const kidId = kid || kids[0]?.id;
  if (!data || (!data.daily && !data.weekly && !data.monthly)) return null;
  const order = async (period) => {
    setBusy(period);
    try { await api.post("/cantine/order", { student_id: kidId, period }); toast.success("Facture créée — payez par Zelle ou en espèces à l'école"); onOrdered && onOrdered(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const opt = (period, label, value, note) => (
    <div className="rounded-md border border-border p-3 bg-background flex flex-col gap-1" data-testid={`parent-cantine-${period}`}>
      <p className="text-sm font-semibold">{label}</p>
      <p className="font-heading font-extrabold text-2xl text-emerald-600">{value ? money(value) : "—"}</p>
      {note ? <p className="text-xs text-muted-foreground">{note}</p> : (
        <Button size="sm" disabled={!value || !kidId || busy === period} onClick={() => order(period)} data-testid={`parent-cantine-order-${period}`}>{busy === period ? "…" : "Commander"}</Button>
      )}
    </div>
  );
  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-5" data-testid="parent-cantine-options">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="font-semibold flex items-center gap-2"><Utensils className="h-4 w-4 text-primary" />Cantine — options de paiement</p>
        {kids.length > 1 && (
          <select className="h-9 text-sm border border-border rounded-md px-2 bg-background" value={kidId} onChange={(e) => setKid(e.target.value)} data-testid="parent-cantine-kid">
            {kids.map((k) => <option key={k.id} value={k.id}>{k.first_name} {k.last_name}</option>)}
          </select>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {opt("daily", "Journalier (1 jour)", data.daily)}
        {opt("weekly", "Hebdomadaire (1 semaine)", data.weekly)}
        {opt("monthly", "Mensuel", data.monthly, "Facturé automatiquement le 1er du mois pour les élèves inscrits.")}
      </div>
    </div>
  );
}
